<template>
  <div class="pv">
    <!-- Loading -->
    <div v-if="loading && !plan" class="pv-state">
      <i class="fa fa-circle-o-notch fa-spin"></i>
      <p>Loading your treatment plan…</p>
    </div>

    <!-- Plan gone: the link is valid but the office has since deleted or re-saved the plan in Open Dental.
         Calm, patient-friendly copy instead of "Couldn't load your plan" + a technical message. -->
    <div v-else-if="planGone" class="pv-state pv-state-error" data-test="pv-plan-gone">
      <i class="fa fa-exclamation-circle"></i>
      <h2>This treatment plan is no longer available</h2>
      <p>Your dental office may have updated your plan. Please contact them for a new link.</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="pv-state pv-state-error">
      <i class="fa fa-exclamation-circle"></i>
      <h2>Couldn't load your plan</h2>
      <p>{{ error }}</p>
    </div>

    <!-- Post-sign success state -->
    <div v-else-if="signSuccess" class="pv-success">
      <div class="pv-success-icon">
        <i class="fa fa-check-circle"></i>
      </div>
      <h2>Plan signed!</h2>
      <!-- Only promise an emailed copy when the practice can actually send one.
           A Core-plan office has no Teamio texting or verified sender, so the
           unconditional version of this line told the patient to watch for an
           email that was never going to arrive. -->
      <p v-if="officeCanNotify">
        Your signed treatment plan is being sent to your office now. You'll receive a copy at
        your email shortly — check your inbox in a few minutes (don't forget the spam folder).
      </p>
      <p v-else>
        Your signed treatment plan has been sent to your office. Contact them directly if
        you'd like a copy for your records.
      </p>
      <div class="pv-success-meta-strong">
        Signed on {{ signedDateLabel }}
      </div>
      <p class="pv-success-meta">
        You can close this window.
      </p>
      <button class="pv-success-btn" @click="onCloseSuccess">View plan again</button>
    </div>

    <!-- Loaded -->
    <div v-else-if="plan" class="pv-paper">
      <!-- Practice header. When the practice has a logo (LogoUrl on
           teamio.sites), show it enlarged and skip the separate name +
           tagline lines — most logos already contain the practice name.
           Without a logo, fall back to the initial-circle + name + tagline
           layout. Same convention as the Builder preview. -->
      <div class="pv-paper-header">
        <div class="pv-practice">
          <img
            v-if="practice.logoUrl"
            :src="practice.logoUrl"
            :alt="practice.name"
            class="pv-practice-logo-large"
          />
          <template v-else>
            <div class="pv-practice-logo">{{ practiceInitial }}</div>
            <div class="pv-practice-meta">
              <div class="pv-practice-name">{{ practice.name }}</div>
              <div class="pv-practice-tag" v-if="practice.tagline">{{ practice.tagline }}</div>
            </div>
          </template>
        </div>
        <button
          class="pv-print-btn pv-no-print"
          type="button"
          title="Print this plan"
          @click="onPrint"
        >
          <i class="fa fa-print"></i>
          <span class="pv-print-label">Print</span>
        </button>
      </div>

      <!-- Slice 11 Phase B — Consent forms callout. Bundled by the TC at send
           time (BundledConsentFormIDs on the presentations row); resolved by
           the backend with patient-specific short URLs. Only renders when
           the patient has at least one form to fill out. Sits ABOVE the
           jump-to-conversation button so it's the first action the patient
           sees on opening the link. -->
      <div v-if="consentForms && consentForms.length > 0" class="pv-consent">
        <div class="pv-consent-icon">
          <i class="fa fa-clipboard"></i>
        </div>
        <div class="pv-consent-body">
          <div class="pv-consent-title">Consent forms to fill out before your appointment</div>
          <p v-if="incompleteConsentForms.length > 0" class="pv-consent-sub">
            Please complete {{ incompleteConsentForms.length === 1 ? 'this form' : 'these forms' }} so we
            can spend more of your visit on care, less on paperwork.
          </p>
          <p v-else class="pv-consent-sub">
            All your consent forms are signed — thank you!
          </p>
          <!-- Slice 12 — completed forms render as non-link "✓ Signed" rows;
               incomplete forms are buttons that open the guided wizard at that
               form. The old target="_blank" link is gone — consent forms only
               ever open in the in-page wizard iframe (no new tabs/windows).
               This works regardless of the practice's guided-flow setting. -->
          <ul class="pv-consent-list">
            <li v-for="f in consentForms" :key="f.formIDForWeb">
              <div v-if="f.completed" class="pv-consent-done">
                <i class="fa fa-check-circle"></i>
                <span class="pv-consent-done-name">{{ f.formName || 'Consent form' }}</span>
                <span class="pv-consent-done-tag">Signed</span>
              </div>
              <button
                v-else
                type="button"
                class="pv-consent-link"
                @click="openWizardAtForm(f.formIDForWeb)"
              >
                <i class="fa fa-file-text-o"></i>
                {{ f.formName || 'Consent form' }}
                <i class="fa fa-chevron-right pv-consent-ext"></i>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Slice 8 — quick-jump button to the conversation thread. The plan can
           be long; without this the patient has to scroll forever to find
           where they posted (or where the office replied). Pulses subtly
           when there are unread office replies so it catches the eye on a
           fresh visit from the notification SMS. -->
      <!-- Both buttons share a flex row so the photos button sits to the LEFT
           of the conversation one. Each keeps its own v-if, so whichever is
           the only one present still lays out correctly on its own. -->
      <div class="pv-actions-row" v-if="(commentsEnabled && hasAnyComments) || showPlanPhotos">
        <button
          v-if="showPlanPhotos"
          class="pv-jump-photos"
          type="button"
          @click="galleryOpen = true"
        >
          <i class="fa fa-camera"></i>
          Photos of Planned Teeth
          <span class="pv-jump-badge">{{ visiblePlanPhotos.length }}</span>
        </button>

        <button
          v-if="commentsEnabled && hasAnyComments"
          class="pv-jump-conversation"
          :class="{ 'pv-jump-pulse': unreadOfficeCount > 0 && !didJumpToConversation }"
          type="button"
          @click="scrollToConversation"
        >
          <i class="fa fa-comments-o"></i>
          Conversation with your office
          <span v-if="unreadOfficeCount > 0 && !didJumpToConversation" class="pv-jump-badge">
            {{ unreadOfficeCount }} new
          </span>
        </button>
      </div>

      <!-- View switcher (patient gets to choose how they read it).
           Hidden when the practice enabled only one view. -->
      <div v-if="enabledViewOptions.length > 1" class="pv-toolbar">
        <div class="pv-view-switcher">
          <button
            v-for="v in enabledViewOptions"
            :key="v.id"
            class="pv-view-tab"
            :class="{ active: activeView === v.id }"
            @click="activeView = v.id"
          >{{ v.label }}</button>
        </div>
      </div>

      <!-- Greeting + intro -->
      <div class="pv-paper-greet" v-if="patient && patient.fName">
        Dear {{ patient.fName }},
      </div>
      <div class="pv-paper-intro">
        Here is the treatment plan we discussed at your visit. Each item below explains
        what we're recommending and why.
      </div>

      <!-- The view itself -->
      <component
        :is="activeViewComponent"
        :procedures="procedures"
        :alts-by-primary="altsByPrimary"
        :label-fn="resolveLabel"
        :reason-fn="resolveReason"
        :show-prices="currentViewShowsPrices"
        :pricing-layers="pricingLayers"
        :group-by-priority="itemizedGroupByPriority"
        :group-label="itemizedGroupLabel"
        :show-group-subtotals="itemizedGroupSubtotals"
        :photo-teeth="photoTeeth"
        @open-tooth-photos="onOpenToothPhotos"
      />

      <!-- Plan-level financial summary. Sits below the view so the
           patient encounters the breakdown after seeing the actual
           procedures (per the design: not the first thing they see).
           The "Show Procedure Discounts" toggle (pricingLayers.membership)
           now governs patient visibility too: when the TC turns it on, the
           per-proc Discount sub-lines already show here, so the summary's
           pre-discount Total Fee + "Practice Discount −$X" row match the
           rows. Toggle off → net-only, no discount anywhere (as before). -->
      <PlanFinancialSummary
        :procedures="procedures"
        :show-prices="currentViewShowsTotals"
        :show-discount="pricingLayers.membership"
        :show-balance="showBalance"
        :family-balance="familyBalance"
        :selected-option="chosenPaymentOption"
        :payment-dates="chosenPaymentDates"
        :downpayment="chosenDownpayment"
        :cherry-terms="cherryTerms"
        :cherry-as-of-text="cherryAsOfText"
      />

      <div v-if="paymentOptionsToShow.length > 0" class="pv-payments">
        <div class="pv-section-head">
          <div class="pv-section-label">Payment options</div>
          <span v-if="savingPreferences" class="pv-saving">
            <i class="fa fa-circle-o-notch fa-spin"></i> Saving…
          </span>
          <span v-else-if="hasAnyPreference" class="pv-saved">
            <i class="fa fa-check-circle"></i> Sent to your office
          </span>
        </div>
        <p v-if="paymentHelperNote" class="pv-payment-helper">{{ paymentHelperNote }}</p>
        <div class="pv-payment-grid">
          <div
            v-for="opt in paymentOptionsToShow"
            :key="opt.id"
            class="pv-payment-card"
            :class="{ recommended: opt.recommended, selected: isSelected(opt.id) }"
          >
            <div v-if="isSelected(opt.id)" class="pv-payment-chosen-badge">
              <i class="fa fa-check-circle"></i> Chosen
            </div>
            <div v-else-if="opt.recommended" class="pv-payment-badge">Most popular</div>
            <div class="pv-payment-title">{{ opt.title }}</div>
            <div class="pv-payment-desc">{{ paymentDesc(opt) }}</div>
            <!-- Multi-tier financing menu — each term row + its estimated
                 monthly. Deliberately NOT gated on currentViewShowsTotals: the
                 tiers ARE the offer, not a price overlay. The card's own price
                 block (down in .pv-payment-foot) is the part that follows the
                 money gate. -->
            <div v-if="opt.type === 'finance' && optFinanceTiers(opt).length" class="pv-finance-tiers">
              <div v-for="(t, ti) in optFinanceTiers(opt)" :key="ti" class="pv-finance-tier">
                <span class="pv-finance-tier-label">{{ t.label }}</span>
                <span class="pv-finance-tier-amt">{{ t.amount }}</span>
              </div>
            </div>
            <!-- A Cherry-backed option: the lender's REAL plans for this exact amount, as
                 the office priced them - or no figures at all. Its breakdown has no typed
                 tiers, so the menu above renders nothing for it. -->
            <CherryTierList
              v-if="isCherryBacked(opt)"
              variant="patient"
              :opt="opt"
              :terms="cherryTerms"
              :amount="optCherryAmount(opt)"
              :as-of-text="cherryAsOfText"
            />
            <!-- Read-only Additional Fee — practice opted in and the office set
                 a per-patient amount in the Builder. Same amber palette as
                 Present Mode + the Plan Summary fee row. -->
            <div v-if="optFeeLabel(opt)" class="pv-payment-fee">
              <i class="fa fa-plus-circle"></i>
              <span class="pv-payment-fee-label">{{ optFeeLabel(opt) }}</span>
              <span class="pv-payment-fee-amt">+{{ formatMoney(optFeeAmount(opt)) }}</span>
            </div>
            <div v-if="opt.terms" class="pv-payment-terms">{{ opt.terms }}</div>
            <!-- Card foot — the money and the CTA, anchored to the bottom of the
                 card (margin-top:auto) so amounts and buttons line up across a
                 row whatever each card carries above them. -->
            <div class="pv-payment-foot">
              <!-- Headline + qualifying sub-line, from the ONE shared helper
                   that also renders the chairside Present Mode card and the
                   signed PDF, so the three can never quote the same plan
                   differently. Gated on currentViewShowsTotals, the same gate
                   the Plan Summary above uses. -->
              <div v-if="currentViewShowsTotals && optAmountLines(opt)" class="pv-payment-amount">
                <span class="pv-pay-amt">{{ optAmountLines(opt).headline }}</span>
                <span v-if="optAmountLines(opt).sub" class="pv-pay-sub">{{ optAmountLines(opt).sub }}</span>
              </div>
              <button
                class="pv-payment-cta"
                :class="{ active: isSelected(opt.id) }"
                :disabled="savingPreferences"
                @click="onToggleOption(opt.id)"
              >
                <i class="fa" :class="isSelected(opt.id) ? 'fa-check-circle' : 'fa-circle-o'"></i>
                {{ isSelected(opt.id) ? 'Chosen' : interestButtonLabel }}
              </button>
            </div>
          </div>
        </div>

        <p class="pv-payments-cta">
          Tap any option you'd consider — your office will see your selections and follow up.
          You can change your mind anytime by tapping again.
        </p>
      </div>

      <!-- Slice 7d — Sign flow -->
      <div v-if="alreadySigned" class="pv-revisit-banner pv-no-print">
        <i class="fa fa-check-circle"></i>
        <div class="pv-revisit-text">
          You signed this plan on <strong>{{ formatSignedDate(latestSignature.signedAt) }}</strong>.
          You can review again anytime, and if anything has changed you can sign again below.
        </div>
      </div>

      <SignaturePanel
        class="pv-no-print"
        :busy="signSubmitting"
        :busy-label="signBusyLabel"
        :error="signError"
        :disclaimer="patientView.disclaimer"
        @submit="onSignSubmit"
        @error="onSignPanelError"
      />

      <!-- "Pay toward balance" ledger (at-home twin). Card-only path: routes to
           the existing public payment page. Renders whenever there's a remaining
           balance or payment history; before the plan is signed it shows a
           "sign first" lock instead of the pay controls. Moolah-setup gating
           stays server-side. -->
      <PatientDepositPanel
        v-if="paymentsEnabled && patient && (remaining > 0 || payments.length)"
        ref="depositPanel"
        :site-i-d="siteID"
        :patient="{ patNum: patNum, fName: patient.fName, lName: patient.lName, WirelessPhone: patient.WirelessPhone, Email: patient.Email }"
        :treat-plan-num="treatPlanNum"
        :suggested-amount="chosenDownpayment"
        :payments="payments"
        :paid-total="paidTotal"
        :remaining="remaining"
        :max-payable="totalFee"
        :timezone="practiceTimezone"
        :signed="hasSignedPlan"
      />

      <!-- Slice 8a/b — Comments / Q&A. Compose box always renders so patients
           can ask their first question; the thread body only renders once
           there's something to show (incl. their just-sent placeholder).
           The whole section is gated on the practice's master comments toggle
           (server-resolved: stored setting AND a verified sender email). When
           off, historical messages are retained in the DB and simply hidden —
           turning it back on restores the thread. The footer's "Questions?
           Contact your dental office." line is the CTA in that state. -->
      <section v-if="commentsEnabled" ref="threadSection" class="pv-thread pv-no-print">
        <h3 class="pv-thread-heading">
          <i class="fa fa-comments-o"></i> Conversation with your office
        </h3>
        <div v-if="threadPatient.comments && threadPatient.comments.length > 0" class="pv-thread-body">
          <CommentsThread
            :comments="threadPatient.comments"
            :loading="threadPatient.loading"
            :error="threadPatient.error || ''"
            :timezone="practiceTimezone"
            viewer-type="patient"
          />
        </div>

        <PatientCompose
          :ai-comments-enabled="threadPatient.aiCommentsEnabled"
          :ai-reply-mode="threadPatient.aiReplyMode || 'auto'"
          :sending="threadPatient.composing.sending"
          :compose-error="threadPatient.composing.error || ''"
          @submit="onPatientCompose"
          @dismiss-error="onDismissComposeError"
        />
      </section>

      <!-- Footer -->
      <div class="pv-footer">
        <div class="pv-footer-practice">{{ practice.name }}</div>
        <div class="pv-footer-meta">
          Questions? Contact your dental office. This plan is private — please don't share the link.
        </div>
      </div>
    </div>

    <!-- Slice 12 — pre-sign nudge. Slim fixed-bottom bar reminding the patient
         about unsigned consent forms; opens the guided wizard. Dismissable for
         the session. Hidden while the wizard is open (redundant) and on the
         post-sign success takeover. -->
    <div v-if="showStickyBar" class="pv-sticky-consent pv-no-print">
      <span class="pv-sticky-text">
        <i class="fa fa-clipboard"></i>
        {{ incompleteConsentForms.length }}
        consent {{ incompleteConsentForms.length === 1 ? 'form needs' : 'forms need' }} your signature
      </span>
      <button type="button" class="pv-sticky-cta" @click="openWizardFromSticky">Sign now</button>
      <button type="button" class="pv-sticky-x" title="Dismiss" @click="dismissSticky">✕</button>
    </div>

    <!-- Slice 12 — post-sign guided wizard. Rendered outside the state branches
         (it's a fixed overlay) so it can sit on top of the paper OR the
         post-sign success takeover. -->
    <PatientNextStepsWizard
      :consent-forms="consentForms"
      :signed="hasSignedPlan"
      :remaining="remaining"
      :payments-enabled="paymentsEnabled"
      :paid-total="paidTotal"
      :open="wizardOpen"
      :initial-form-id="wizardInitialFormId"
      :treat-plan-num="treatPlanNum"
      @form-completed="onWizardFormCompleted"
      @go-to-payment="onWizardGoToPayment"
      @close="onWizardClose"
    />

    <!-- Photo Attachments — same gallery and viewer the TC uses chairside, so
         the patient sees at home exactly what they were shown in the chair. -->
    <PlanPhotosGallery
      v-if="galleryOpen"
      :photos="visiblePlanPhotos"
      @close="galleryOpen = false"
      @open="onOpenGalleryPhoto"
      @refresh-urls="onRefreshPhotoUrls"
    />

    <PhotoLightbox
      v-if="lightboxPhotos.length > 0"
      :photos="lightboxPhotos"
      :start-index="lightboxIndex"
      @close="lightboxPhotos = []"
      @refresh-urls="onRefreshPhotoUrls"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
// moment-timezone (not bare moment) so we can convert UTC backend timestamps
// into the practice's timezone for display per the project's timezone rule.
import moment from 'moment-timezone';
import ItemizedView from '@/pages/TxPresenter/components/views/ItemizedView.vue';
import ArchRegionView from '@/pages/TxPresenter/components/views/ArchRegionView.vue';
import PlanFinancialSummary from '@/pages/TxPresenter/components/PlanFinancialSummary.vue';
import { interpolatePaymentDesc, computePaymentBreakdown, resolveFeeAmount, formatFeeLabel, financeTierLabel, paymentOptionAmountLines } from '@/pages/TxPresenter/utils/paymentOptions.js';
import { isCherryBacked, cherryAmountFor, cherryAsOfText } from '@/pages/TxPresenter/utils/cherryTerms.js';
import { patientPortion as sumPatientPortion } from '@/pages/TxPresenter/utils/patientPortion.js';
import CherryTierList from '@/pages/TxPresenter/components/CherryTierList.vue';
import { isDiagnosticCode } from '@/pages/TxPresenter/utils/procedureOrder.js';
import SignaturePanel from './components/SignaturePanel.vue';
import PatientDepositPanel from './components/PatientDepositPanel.vue';
import CommentsThread from '@/pages/TxPresenter/components/CommentsThread.vue';
import PatientCompose from './components/PatientCompose.vue';
import PatientNextStepsWizard from './components/PatientNextStepsWizard.vue';
import PlanPhotosGallery from '@/pages/TxPresenter/components/photos/PlanPhotosGallery.vue';
import PhotoLightbox from '@/pages/TxPresenter/components/photos/PhotoLightbox.vue';
import {
  planToothNums, filterPhotosForPlan, photoTeethMap, photosForTooth,
} from '@/pages/TxPresenter/utils/photoFilter.js';

export default {
  name: 'TxPatientView',
  components: { ItemizedView, ArchRegionView, PlanFinancialSummary, CherryTierList, SignaturePanel, PatientDepositPanel, CommentsThread, PatientCompose, PatientNextStepsWizard, PlanPhotosGallery, PhotoLightbox },

  data() {
    return {
      activeView: 'region',
      viewOptions: [
        { id: 'region', label: 'By Region' },
        { id: 'itemized', label: 'Itemized' },
      ],
      // Slice 8 — once the patient clicks the jump button (or sends a
      // message that auto-scrolls), drop the "N new" badge so it doesn't
      // keep nagging within the same session.
      didJumpToConversation: false,

      // Photo Attachments. `lightboxPhotos` non-empty IS the open state.
      galleryOpen: false,
      lightboxPhotos: [],
      lightboxIndex: 0,

      // Slice 8e — focus-only thread polling.
      // Patient view polls continuously (when page visible) since the patient
      // is on the page reading and waiting for replies — there's no panel to
      // toggle. Visibility listener stays bound for the component's lifetime.
      threadPollInterval: null,
      threadPollVisibilityHandler: null,
      THREAD_POLL_MS: 10000,

      // Slice 12 — post-sign guided wizard.
      // `wizardOpen` — overlay visibility. `wizardInitialFormId` — the consent
      // form to land on when opening from a banner button (null = first
      // incomplete form / auto-open after signing). `stickyDismissed` — the
      // pre-sign consent nudge bar was dismissed this session.
      wizardOpen: false,
      wizardInitialFormId: null,
      stickyDismissed: false,
    };
  },

  computed: {
    ...mapState('txPresenter', {
      patientView: (s) => s.patientView,
      threadPatient: (s) => s.threadPatient,
    }),
    ...mapGetters('txPresenter', ['patientResolvedLabel', 'patientResolvedReason']),

    loading() { return this.patientView.loading; },
    error() { return this.patientView.error; },
    planGone() { return !!this.patientView.planGone; },
    practice() { return this.patientView.practice || { name: '', tagline: '' }; },

    /**
     * Master gate for the conversation section, resolved SERVER-side and handed
     * over in getTxPatientView's `thread.commentsEnabled` (stored setting AND a
     * verified sender email). This page is public — it has no customer.settings
     * and no siteID auth — so the flag cannot be computed here; the store
     * default-denies when the key is absent (old backend).
     */
    commentsEnabled() {
      return this.threadPatient.commentsEnabled === true;
    },
    /**
     * Slice 8 — true if there's any back-and-forth on this plan (patient
     * questions, AI replies, or office replies). The jump button only renders
     * once at least one comment exists.
     */
    hasAnyComments() {
      return !!(this.threadPatient.comments && this.threadPatient.comments.length > 0);
    },
    /**
     * Slice 8 — count of office/AI messages the patient hasn't read yet.
     * Backend's getTxPatientView fires _markThreadReadByPatient AFTER the
     * thread fetch so the rows we receive still have null readByPatientAt
     * for anything new since the last visit. Drives the "N new" badge.
     */
    unreadOfficeCount() {
      const list = (this.threadPatient.comments || []);
      return list.filter((c) =>
        (c.author === 'office' || c.author === 'ai')
        && !c.readByPatientAt
      ).length;
    },
    /**
     * Slice 8d (timezone fix) — IANA tz string from getTxPatientView's
     * practice.timezone (sourced from settings GENERAL_TIMEZONE). Empty
     * string falls through to browser local in the formatters below;
     * CommentsThread does the same fallback internally.
     */
    practiceTimezone() {
      return (this.practice && this.practice.timezone) || '';
    },
    patient() { return this.patientView.patient; },
    plan() { return this.patientView.plan; },
    // ─── Photo Attachments ───────────────────────────────────────────────
    /**
     * The photos this link is allowed to show.
     *
     * Comes from /get-tx-patient-photos, NOT from the main payload — and the
     * server has already applied the showPhotos flag, returning an empty list
     * (and signing nothing) when it's off. `photosEnabled` is its answer, so
     * trust it rather than re-deriving from pricingDisplay here.
     */
    patientViewPhotos() {
      return this.patientView.photos || [];
    },
    visiblePlanPhotos() {
      if (!this.patientView.photosEnabled) return [];
      return filterPhotosForPlan(this.patientViewPhotos, planToothNums(this.procedures));
    },
    showPlanPhotos() {
      return this.visiblePlanPhotos.length > 0;
    },
    photoTeeth() {
      if (!this.patientView.photosEnabled) return {};
      return photoTeethMap(this.visiblePlanPhotos, planToothNums(this.procedures));
    },

    procedures() {
      const all = this.patientView.procedures || [];
      // Honor the TC's per-patient "Show exams & X-rays" setting. Backend now
      // returns diagnostic codes; hide them here when the setting is off.
      const pd = this.patientView.pricingDisplay || {};
      const showDiagnostic = pd.showDiagnostic !== false;
      if (showDiagnostic) return all;
      return all.filter((p) => !isDiagnosticCode(p.procCode));
    },
    altsByPrimary() { return this.patientView.altsByPrimary; },
    totalFee() { return this.patientView.totalFee; },
    paymentOptions() { return this.patientView.paymentOptions; },

    paymentOptionsToShow() {
      if (!this.paymentOptions || !Array.isArray(this.paymentOptions.options)) return [];
      return this.paymentOptions.options.filter((o) => o.enabled);
    },
    paymentHelperNote() { return this.paymentOptions && this.paymentOptions.helperNote; },

    // "Show balance/credits from Open Dental" — the backend already resolved
    // this via mergePricingDisplay (per-patient → practice → hardcoded, default
    // ON); `!== false` also covers pre-slice plans that send a null pricingDisplay.
    showBalance() {
      const pd = this.patientView.pricingDisplay || {};
      return pd.showBalance !== false;
    },
    // Guarantor account balance / credit (BalTotal − InsEst) frozen into the load.
    familyBalance() {
      return (this.patientView && this.patientView.familyBalance) || 0;
    },
    // Balance folded into the patient portion — 0 when the toggle is off.
    balanceAmount() {
      return this.showBalance ? (parseFloat(this.familyBalance) || 0) : 0;
    },
    // Patient out-of-pocket — base for the per-card payment math. Includes the
    // guarantor balance / credit when showBalance is on, matching the Plan
    // Summary green bar the patient sees.
    patientPortion() {
      return sumPatientPortion(this.procedures, this.balanceAmount);
    },
    // Cherry's real plans as the office priced them (stored by the backend; this public page
    // can't price anything). Null => the Cherry card shows no figures.
    cherryTerms() { return this.patientView.cherryTerms || null; },
    // "October 10, 2026 at 11:00 AM" - when Cherry priced them, in the practice's timezone.
    cherryAsOfText() {
      return cherryAsOfText(this.cherryTerms, this.practiceTimezone);
    },
    // The TC's chosen option (set chairside in Present Mode), reflected here so
    // the at-home Plan Summary shows the same breakdown. Read-only at home.
    chosenPaymentDates() {
      const pd = this.patientView.pricingDisplay || {};
      return Array.isArray(pd.chosenPaymentDates) ? pd.chosenPaymentDates : [];
    },
    // Chairside "Pay today" amount, persisted on the plan and mirrored here.
    chosenDownpayment() {
      const pd = this.patientView.pricingDisplay || {};
      const n = parseFloat(pd.chosenDownpayment);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    chosenPaymentOptionId() {
      return (this.patientView.pricingDisplay && this.patientView.pricingDisplay.chosenPaymentOptionId) || null;
    },
    // The patient's single effective selection. Their own pick (preferences.selectedIds[0])
    // wins; before they tap anything (empty) it falls back to the option the TC chose
    // chairside (chosenPaymentOptionId), so the cards + PLAN SUMMARY reflect it on load.
    // Both the option cards (isSelected) and the summary (chosenPaymentOption) read this one
    // value, so a tap updates the summary live — matching Present mode.
    effectiveSelectedId() {
      const ids = (this.preferences && this.preferences.selectedIds) || [];
      return (Array.isArray(ids) && ids.length > 0 ? ids[0] : null) || this.chosenPaymentOptionId;
    },
    chosenPaymentOption() {
      if (!this.effectiveSelectedId) return null;
      return this.paymentOptionsToShow.find((o) => o.id === this.effectiveSelectedId) || null;
    },
    interestButtonLabel() {
      return (this.paymentOptions && this.paymentOptions.buttonLanguage) || "I'm interested";
    },

    preferences() { return this.patientView.preferences; },
    savingPreferences() { return this.patientView.savingPreferences; },
    hasAnyPreference() {
      const p = this.preferences || {};
      return Array.isArray(p.selectedIds) && p.selectedIds.length > 0;
    },

    // Slice 7d — sign flow state
    signState() { return this.$store.state.txPresenter.sign; },
    signSubmitting() { return this.signState.submitting; },
    signStage() { return this.signState.stage; },
    signError() { return this.signState.error || ''; },
    signSuccess() { return this.signState.stage === 'done'; },
    signedDateLabel() {
      // Slice 8d timezone fix — backend returns signedAt as UTC ISO. Display
      // in practice timezone so the patient sees the office's local time.
      const tz = this.practiceTimezone && moment.tz.zone(this.practiceTimezone) ? this.practiceTimezone : null;
      const iso = this.signState.result && this.signState.result.signedAt;
      const m = iso ? (tz ? moment.utc(iso).tz(tz) : moment(iso))
                    : (tz ? moment.tz(tz) : moment());
      return m.format('MMMM D, YYYY [at] h:mm A');
    },
    signBusyLabel() {
      // Backend renders + emails asynchronously now, so the only stage the
      // patient sees from the spinner is the network round-trip — typically
      // under a second. Keep one friendly label.
      return 'Sending…';
    },
    latestSignature() { return this.patientView.latestSignature; },
    // Slice 11 Phase B — bundled consent forms. Empty array when nothing
    // bundled or when the practice didn't send any with this plan.
    consentForms() { return this.patientView.consentForms || []; },
    /**
     * Server-resolved per-site capability (this page is public — no store settings).
     *
     * paymentsEnabled false HIDES the payment section entirely rather than greying
     * it. The patient isn't a Teamio customer: they can't act on "your office isn't
     * enabled for payments", it exposes the practice's vendor status, and it lands
     * exactly while they're deciding to accept treatment. Previously the panel
     * rendered on balance alone and the patient only discovered the problem after
     * entering an amount and clicking Pay.
     */
    paymentsEnabled() { return this.patientView.paymentsEnabled === true; },
    /** Whether the office can reach this patient by text or email at all. */
    officeCanNotify() { return this.patientView.officeCanNotify === true; },
    // Slice 12 — consent forms the patient hasn't completed yet. `completed`
    // may be undefined on older payloads; treat undefined as false (incomplete).
    incompleteConsentForms() {
      return this.consentForms.filter((f) => !f.completed);
    },
    // Slice 12 — practice opt-out for the post-sign guided wizard + pre-sign
    // nudge bar. May be undefined on older payloads; only an explicit `false`
    // disables (undefined is treated as enabled).
    guidedFlowEnabled() {
      return this.practice.guidedFlowEnabled !== false;
    },
    // Slice 12 — per-plan sessionStorage key so dismissing the nudge on one
    // plan doesn't hide it on another the patient opens in the same session.
    stickyKey() {
      return 'txp-consent-sticky-dismissed:' + (this.$route.params.shortId || '');
    },
    // Slice 12 — the pre-sign nudge bar shows only on the loaded paper (not the
    // loading/error/success-takeover states), when guided flow is on, there are
    // unsigned consents, the wizard is closed, and it wasn't dismissed.
    showStickyBar() {
      if (!this.plan || this.signSuccess || this.error) return false;
      return this.guidedFlowEnabled
        && this.incompleteConsentForms.length > 0
        && !this.wizardOpen
        && !this.stickyDismissed;
    },
    alreadySigned() { return !!this.latestSignature; },
    // Pay-deposit panel state ─────────────────────────────────
    /** True if the plan was just signed OR was signed in a prior session. */
    hasSignedPlan() { return this.signSuccess || this.alreadySigned; },
    /** Patient/plan identifiers resolved server-side from the short URL. */
    resolved() { return this.patientView.resolved || {}; },
    patNum() { return this.resolved.patNum || null; },
    treatPlanNum() { return this.resolved.treatPlanNum || null; },
    siteID() { return this.resolved.siteID || ''; },
    /** Existing deposit state from the plan's pricingDisplay (read-only audit
     *  line when the chairside path already recorded a collection). */
    depositState() {
      const pd = this.patientView.pricingDisplay || {};
      return {
        depositPaymentID: pd.depositPaymentID || null,
        depositCollectedAt: pd.depositCollectedAt || null,
        depositMethod: pd.depositMethod || null,
        depositAmount: pd.depositAmount || 0,
      };
    },
    // "Pay toward balance" ledger (Slice 2) — paid payments + remaining for this plan.
    payments() { return this.patientView.payments || []; },
    paidTotal() { return this.patientView.paidTotal || 0; },
    // Remaining against the VISIBLE patient portion (which folds in the guarantor
    // balance / credit when showBalance is on), minus what's been paid — mirrors
    // Present Mode's paymentRemaining so the at-home "Amount to Pay" matches the
    // portion the patient sees. The backend `remaining` is plan-only (no balance).
    remaining() {
      return Math.max(0, Math.round((this.patientPortion - this.paidTotal) * 100) / 100);
    },

    activeViewComponent() {
      switch (this.activeView) {
        case 'itemized': return 'ItemizedView';
        case 'region':
        default: return 'ArchRegionView';
      }
    },

    /**
     * Which views the patient may switch between, per the practice's
     * Settings → General (sent in the payload as viewConfig.enabledViews).
     * Falls back to both when the backend predates this feature. The
     * switcher hides entirely when only one is enabled.
     */
    enabledViewOptions() {
      const vc = this.patientView.viewConfig || {};
      const enabled = (Array.isArray(vc.enabledViews) && vc.enabledViews.length)
        ? vc.enabledViews
        : ['region', 'itemized'];
      return this.viewOptions.filter((o) => enabled.includes(o.id));
    },

    /**
     * Two-level pricing display. Patient honors what the TC set in the
     * Builder right pane (per-view pricing mode + layer breakdowns).
     *
     * The payload is ALREADY server-resolved (mergePricingDisplay), so this
     * reads the resolved keys rather than re-running the shared resolver.
     * `pricing` falls back to the legacy `viewVisibility` boolean: plans
     * authored before either slice send neither, and a bundle that reaches a
     * browser ahead of the backend deploy sees only viewVisibility.
     */
    viewPricingModes() {
      const pd = this.patientView.pricingDisplay || {};
      const modes = pd.pricing || {};
      const legacy = pd.viewVisibility || {};
      const resolve = (view) => {
        if (modes[view] === 'full' || modes[view] === 'total' || modes[view] === 'none') return modes[view];
        return legacy[view] === false ? 'none' : 'full';
      };
      return { region: resolve('region'), itemized: resolve('itemized') };
    },
    pricingLayers() {
      const pd = this.patientView.pricingDisplay || {};
      const layers = pd.layers || {};
      return {
        netFee: layers.netFee !== false,
        standardFee: layers.standardFee !== false,
        insuranceEstimate: layers.insuranceEstimate !== false,
        membership: layers.membership !== false,
      };
    },
    /** PER-ROW fees in the active view. */
    currentViewShowsPrices() {
      return this.viewPricingModes[this.activeView] === 'full';
    },
    /**
     * The Plan Summary card — separate gate, so 'total' keeps the plan total
     * while dropping per-row fees. Do not collapse these two back together.
     */
    currentViewShowsTotals() {
      return this.viewPricingModes[this.activeView] !== 'none';
    },
    itemizedGroupByPriority() {
      const pd = this.patientView.pricingDisplay || {};
      return pd.itemizedGroupByPriority === true;
    },
    itemizedGroupLabel() {
      const pd = this.patientView.pricingDisplay || {};
      return (pd.itemizedGroupLabel && String(pd.itemizedGroupLabel).trim()) || 'Priority';
    },
    itemizedGroupSubtotals() {
      const pd = this.patientView.pricingDisplay || {};
      return pd.itemizedGroupSubtotals !== false; // default true
    },

    practiceInitial() {
      const n = (this.practice.name || 'P').trim();
      return (n.charAt(0) || 'P').toUpperCase();
    },

    resolveLabel() { return (proc) => this.patientResolvedLabel(proc); },
    resolveReason() { return (proc) => this.patientResolvedReason(proc); },
  },

  watch: {
    '$route.params.shortId': {
      immediate: true,
      handler(shortId) {
        if (shortId) this.load(shortId);
      },
    },
    // Slice 8e — start polling once the patient view has actually loaded
    // (we have a shortId on state). Stops if the page navigates away.
    'patientView.shortId': {
      immediate: true,
      handler(shortId) {
        if (shortId) this.startThreadPolling();
        else this.stopThreadPolling();
      },
    },
    // Open the practice's default layout once the payload arrives. Coerces a
    // legacy 'phase' default and guarantees the result is an enabled view.
    'patientView.viewConfig': {
      immediate: true,
      handler(vc) {
        if (!vc) return;
        const enabled = (Array.isArray(vc.enabledViews) && vc.enabledViews.length)
          ? vc.enabledViews
          : ['region', 'itemized'];
        let dv = vc.defaultView || 'region';
        if (dv === 'phase') dv = 'region';
        this.activeView = enabled.includes(dv) ? dv : enabled[0];
      },
    },
    // Slice 12 — auto-open the guided wizard on a FRESH sign success (stage
    // transitioning to 'done' this session). Not immediate — a revisit that
    // loads with an existing latestSignature keeps the sign stage at 'idle',
    // so this never fires on already-signed loads. Only opens when guided flow
    // is on and there's actually something to guide (unsigned consents, or a
    // remaining balance the practice can actually COLLECT — `paymentsEnabled`
    // must gate the balance arm in lockstep with the wizard's own
    // `hasPaymentStep`, or a payments-off practice auto-opens a wizard with
    // zero steps in it).
    signStage(newStage, oldStage) {
      if (newStage === 'done' && oldStage !== 'done') {
        if (this.guidedFlowEnabled && (this.incompleteConsentForms.length > 0 || (this.paymentsEnabled && this.remaining > 0))) {
          this.wizardInitialFormId = null;
          this.wizardOpen = true;
        }
      }
    },
  },

  mounted() {
    // Slice 8e — pause polling when the tab is hidden (e.g., patient
    // switches tabs) so we don't burn Lambda invocations on backgrounded
    // tabs. Resume + immediate poll on tab-refocus so any office reply
    // sent while away appears right away.
    this.threadPollVisibilityHandler = () => {
      if (!this.patientView.shortId) return;
      if (document.hidden) {
        this.stopThreadPolling();
      } else {
        this.startThreadPolling({ pollImmediately: true });
      }
    };
    document.addEventListener('visibilitychange', this.threadPollVisibilityHandler);

    // Slice 12 — restore the pre-sign nudge dismissal for this plan/session.
    try {
      if (sessionStorage.getItem(this.stickyKey) === '1') this.stickyDismissed = true;
    } catch (e) { /* sessionStorage unavailable — nudge just stays visible */ }
  },

  beforeDestroy() {
    if (this.threadPollVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.threadPollVisibilityHandler);
      this.threadPollVisibilityHandler = null;
    }
    this.stopThreadPolling();
  },

  methods: {
    paymentDesc(opt) {
      const extras = (opt && opt.id === this.chosenPaymentOptionId)
        ? { downpayment: this.chosenDownpayment }
        : null;
      return interpolatePaymentDesc(opt, extras);
    },
    // Read-only fee preview ($/% label + resolved dollar amount).
    optFeeAmount(opt) { return resolveFeeAmount(opt, this.patientPortion); },
    optFeeLabel(opt) { return this.optFeeAmount(opt) > 0 ? formatFeeLabel(opt) : ''; },

    // Print the plan as it's currently shown (active view). The @media print
    // stylesheet strips the interactive chrome and formats it as a document.
    onPrint() { window.print(); },

    async load(shortId) {
      // Photos go in parallel and are never awaited into the same try: a photo
      // failure must not stop the plan from rendering. The action swallows its
      // own errors, so this can't reject.
      this.$store.dispatch('txPresenter/loadPatientViewPhotos', { shortId });
      try {
        await this.$store.dispatch('txPresenter/loadPatientView', { shortId });
      } catch (e) { /* error in state */ }
    },

    // ─── Photo Attachments ───
    onOpenToothPhotos(toothNum) {
      const forTooth = photosForTooth(this.visiblePlanPhotos, toothNum);
      if (forTooth.length === 0) return;
      this.lightboxPhotos = forTooth;
      this.lightboxIndex = 0;
    },
    onOpenGalleryPhoto(index) {
      this.lightboxPhotos = this.visiblePlanPhotos.slice();
      this.lightboxIndex = index || 0;
    },
    /**
     * Re-mint expired presigned URLs. This page is the one most likely to need
     * it — a patient opens the link from an SMS, reads a bit, puts the phone
     * down, and comes back an hour later to the same tab.
     */
    async onRefreshPhotoUrls() {
      const shortId = this.$route.params.shortId;
      if (!shortId) return;
      const openIds = this.lightboxPhotos.map((p) => p.photoId);
      await this.$store.dispatch('txPresenter/loadPatientViewPhotos', { shortId });
      if (openIds.length > 0) {
        this.lightboxPhotos = this.patientViewPhotos.filter((p) => openIds.indexOf(p.photoId) !== -1);
      }
    },

    // ─── Slice 8e — focus-only thread polling ───
    startThreadPolling({ pollImmediately = false } = {}) {
      // Nothing to poll when the practice has comments off — the section isn't
      // rendered and the backend answers with an empty page. Both callers (the
      // shortId watcher and the visibilitychange handler) route through here.
      if (!this.commentsEnabled) return;
      if (this.threadPollInterval) return;
      if (document.hidden) return;
      if (pollImmediately) {
        this.$store.dispatch('txPresenter/pollThreadPatient').catch(() => {});
      }
      this.threadPollInterval = setInterval(() => {
        if (document.hidden) return;
        this.$store.dispatch('txPresenter/pollThreadPatient').catch(() => {});
      }, this.THREAD_POLL_MS);
    },
    stopThreadPolling() {
      if (this.threadPollInterval) {
        clearInterval(this.threadPollInterval);
        this.threadPollInterval = null;
      }
    },

    isSelected(optionId) {
      return optionId === this.effectiveSelectedId;
    },

    async onToggleOption(optionId) {
      // Single-select (mirror Present mode): tapping an option replaces the selection;
      // tapping the already-selected one clears it (reverts to the TC's choice via the
      // effectiveSelectedId fallback). Stored as a 0–1-length array so any office-side
      // consumer that reads selectedIds keeps working.
      const next = optionId === this.effectiveSelectedId ? [] : [optionId];
      try {
        await this.$store.dispatch('txPresenter/savePaymentPreference', {
          selectedIds: next,
          otherNote: '',
        });
      } catch (e) { /* error in state */ }
    },
    formatMoney(n) {
      if (!n) return '—';
      return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    /**
     * The headline + sub-line of money printed ON the card, computed against
     * the patient portion (after insurance) via the shared helper — so the
     * at-home card, the chairside Present Mode card and the signed PDF all
     * quote the plan in the same words. When this option is the TC's chosen one
     * and a chairside "Pay today" was entered, the math reflects that (the
     * remainder is what gets split/financed). Returns null for a card with no
     * honest number to show (custom option, zero total, tier-less financing).
     */
    optAmountLines(opt) {
      const extras = (opt && opt.id === this.chosenPaymentOptionId)
        ? { downpayment: this.chosenDownpayment }
        : undefined;
      return paymentOptionAmountLines(opt, this.patientPortion, extras, (n) => this.formatMoney(n), this.cherryTerms);
    },

    /** Template access to the shared predicate — see utils/cherryTerms.js. */
    isCherryBacked(opt) {
      return isCherryBacked(opt);
    },
    /** The amount this Cherry card's terms must have been priced for (same breakdown as the card). */
    optCherryAmount(opt) {
      const extras = (opt && opt.id === this.chosenPaymentOptionId)
        ? { downpayment: this.chosenDownpayment }
        : undefined;
      return cherryAmountFor(computePaymentBreakdown(opt, this.patientPortion, extras));
    },

    // Multi-tier financing rows for the card: [{ label, amount }] each with an
    // estimated monthly. Shares financeTierLabel + the breakdown with Present
    // Mode + the signed PDF so all three render the same menu.
    optFinanceTiers(opt) {
      if (!opt || opt.type !== 'finance') return [];
      const extras = (opt.id === this.chosenPaymentOptionId)
        ? { downpayment: this.chosenDownpayment }
        : undefined;
      const b = computePaymentBreakdown(opt, this.patientPortion, extras);
      return (b.tiers || []).map((t) => ({
        label: financeTierLabel(t),
        amount: `~${this.formatMoney(Math.round(t.monthly))}/mo`,
      }));
    },

    // ─── Slice 7d — Sign flow ────────────────────────
    formatSignedDate(iso) {
      if (!iso) return '';
      // Slice 8d timezone fix — UTC ISO from backend, displayed in the
      // practice's timezone (revisit banner: "You signed this plan on …").
      const tz = this.practiceTimezone && moment.tz.zone(this.practiceTimezone) ? this.practiceTimezone : null;
      const m = tz ? moment.utc(iso).tz(tz) : moment(iso);
      if (!m.isValid()) return '';
      return m.format('MMM D, YYYY');
    },

    onSignPanelError(message) {
      this.$store.commit('txPresenter/SET_SIGN_ERROR', message || 'Something went wrong.');
    },

    /** Patient picked "I'll pay later" on the at-home deposit panel.
     *  Just a UX dismiss — no persistence (the patient can re-open the link
     *  and pay later via the same panel). The office can also send a
     *  follow-up SMS pay link via Patient Profile when ready. */
    onDepositLater() {
      // Intentionally a no-op for v1 — the panel hides itself via local state.
    },

    async onSignSubmit({ signerName, signatureDataUrl, acknowledgedIds }) {
      // After the @react-pdf migration the browser no longer renders the PDF
      // — we just hand the signature image to the backend and let it handle
      // S3 + email asynchronously. `acknowledgedIds` = the office's checkbox
      // lines the patient ticked (the server rejects a missing required one).
      try {
        await this.$store.dispatch('txPresenter/submitSignFlow', {
          signerName,
          signatureDataUrl,
          acknowledgedIds,
        });
      } catch (e) { /* error already in store */ }
    },

    onCloseSuccess() {
      // Reset sign state and reload patient view to pick up fresh latestSignature
      this.$store.dispatch('txPresenter/resetSign');
      if (this.$route.params.shortId) {
        this.load(this.$route.params.shortId);
      }
    },

    // Slice 8b — patient compose
    async onPatientCompose({ body }) {
      // Slice 8 UX — scroll the page to the conversation BEFORE we await
      // the network round-trip so the patient instantly sees their own
      // bubble appear via the optimistic insert. Without this, the
      // patient's first feedback is the button changing to "Sending…"
      // while the bubble lands somewhere far below the fold.
      this.scrollToConversation();
      try {
        await this.$store.dispatch('txPresenter/postPatientComment', { body });
      } catch (e) { /* error already in store; component shows it */ }
    },
    onDismissComposeError() {
      this.$store.dispatch('txPresenter/clearComposeError');
    },

    /**
     * Slice 8 — smooth-scroll the page to the thread section. Called from
     * the top-of-page jump button AND automatically right after the patient
     * hits Send (so they see their bubble appear without manually scrolling
     * a long plan).
     *
     * `block: 'end'` aligns the BOTTOM of the section with the bottom of the
     * viewport. The section contains: heading → thread body (already
     * auto-scrolled to its newest bubble inside CommentsThread) → compose
     * box. End-aligned means the compose box sits at viewport bottom + the
     * latest comment is visible just above. `block: 'start'` was wrong —
     * it puts the heading at viewport top and pushes the new bubble +
     * compose below the fold.
     */
    scrollToConversation() {
      this.didJumpToConversation = true; // drops the "N new" badge
      this.$nextTick(() => {
        const el = this.$refs.threadSection;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
    },

    // ─── Slice 12 — guided wizard + pre-sign nudge ───
    /** Open the wizard starting on a specific consent form (banner button).
     *  Intentionally NOT gated by guidedFlowEnabled — opening a form in the
     *  in-page iframe is the only way to view it (no new tabs), so it must
     *  always work. */
    openWizardAtForm(formIDForWeb) {
      this.wizardInitialFormId = formIDForWeb || null;
      this.wizardOpen = true;
    },
    /** Open the wizard from the pre-sign nudge bar (starts at the first
     *  incomplete form). */
    openWizardFromSticky() {
      this.wizardInitialFormId = null;
      this.wizardOpen = true;
    },
    /** Dismiss the pre-sign nudge for this plan/session. */
    dismissSticky() {
      this.stickyDismissed = true;
      try {
        sessionStorage.setItem(this.stickyKey, '1');
      } catch (e) { /* sessionStorage unavailable — no-op */ }
    },
    /** A form was submitted inside the wizard — refetch the patient view so
     *  `completed` flips true on that form (the wizard keeps its own snapshot,
     *  so this refetch doesn't disrupt the in-flight step sequence). */
    onWizardFormCompleted() {
      if (this.$route.params.shortId) this.load(this.$route.params.shortId);
    },
    /** Patient chose "Make a payment" on the wizard's payment step. Close the
     *  wizard and reveal the deposit panel. When we're on the post-sign success
     *  takeover the paper (and thus the panel) isn't in the DOM, so drop the
     *  success state + reload first, then scroll once the paper renders. */
    onWizardGoToPayment() {
      this.wizardOpen = false;
      if (this.signSuccess) {
        this.$store.dispatch('txPresenter/resetSign');
        const shortId = this.$route.params.shortId;
        const reloaded = shortId ? this.load(shortId) : Promise.resolve();
        Promise.resolve(reloaded).then(() => this.scrollToDepositPanel());
      } else {
        this.scrollToDepositPanel();
      }
    },
    onWizardClose() {
      this.wizardOpen = false;
    },
    /** Smooth-scroll to the "Pay toward balance" panel (mirrors
     *  scrollToConversation). `$refs.depositPanel` is a component, so scroll
     *  its root element. */
    scrollToDepositPanel() {
      this.$nextTick(() => {
        const ref = this.$refs.depositPanel;
        const el = ref && ref.$el ? ref.$el : ref;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
  },
};
</script>

<style scoped>
.pv {
  min-height: 100vh;
  background: #f7f8fa;
  padding: 28px 18px 60px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
}
.pv * { box-sizing: border-box; }

.pv-state {
  max-width: 480px;
  margin: 80px auto;
  text-align: center;
  color: #475569;
  font-size: 14px;
}
.pv-state .fa { font-size: 28px; color: #94a3b8; margin-bottom: 14px; }
.pv-state h2 { font-size: 18px; font-weight: 600; margin: 6px 0 8px; }
.pv-state-error .fa { color: #ef4444; }

.pv-paper {
  max-width: 760px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.04);
  padding: 32px 36px 36px;
}

.pv-paper-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 18px;
}
.pv-practice {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.pv-practice-logo {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #0d5f54;
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  font-family: Georgia, 'Times New Roman', serif;
  flex-shrink: 0;
}
/* Enlarged logo replaces the circle + name + tagline when LogoUrl is set.
   Same constraints as the Builder for visual parity. */
.pv-practice-logo-large {
  max-height: 64px;
  max-width: 280px;
  object-fit: contain;
  display: block;
}
.pv-practice-name {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: #0d5f54;
}
.pv-practice-tag {
  font-size: 10px;
  color: #6b7785;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-top: 2px;
  font-weight: 500;
}
.pv-paper-meta {
  text-align: right;
  font-size: 12px;
  color: #475569;
}
.pv-paper-date { font-weight: 500; }

/* Print button (top-right of the paper header) */
.pv-print-btn {
  margin-left: auto;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  background: #fff;
  border: 1px solid #d4cfc3;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #0d5f54;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.pv-print-btn:hover { background: #f0f7f6; border-color: #0d5f54; }
.pv-print-btn .fa { font-size: 13px; }
@media (max-width: 600px) {
  .pv-print-label { display: none; }
}

/* Slice 8 — quick-jump CTA to the conversation thread */
/* Photo Attachments — the two quick-jump buttons share a row, photos on the
   left. Wraps on a narrow phone rather than shrinking either button, since
   both labels are full sentences the patient is meant to read. The 14px
   bottom margin moved off the buttons and onto the row so a single-button
   case still spaces correctly. */
.pv-actions-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px;
}

.pv-jump-photos {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 16px;
  background: #ffffff;
  border: 1px solid #d4cfc3;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #0d5f54;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
}
.pv-jump-photos:hover {
  background: #f0f7f6;
  border-color: #0d5f54;
}
.pv-jump-photos:active { transform: translateY(1px); }
.pv-jump-photos .fa { color: #0d5f54; }

.pv-jump-conversation {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 16px;
  background: #ffffff;
  border: 1px solid #d4cfc3;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #0d5f54;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
}
.pv-jump-conversation:hover {
  background: #f0f7f6;
  border-color: #0d5f54;
}
.pv-jump-conversation:active { transform: translateY(1px); }
.pv-jump-conversation .fa { color: #0d5f54; }
.pv-jump-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  background: #0d5f54;
  color: #fff;
  border-radius: 999px;
  margin-left: 4px;
}
/* Slice 8 — pulse to draw attention when there's a new office reply */
.pv-jump-pulse {
  background: #fff8eb;
  border-color: #f59e0b;
  color: #92400e;
  animation: pv-jump-pulse-anim 2s ease-in-out infinite;
}
.pv-jump-pulse .fa { color: #92400e; }
.pv-jump-pulse .pv-jump-badge { background: #f59e0b; color: #fff; }
@keyframes pv-jump-pulse-anim {
  0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.45); }
  50%      { box-shadow: 0 0 0 8px rgba(245, 158, 11, 0);   }
}

/* Slice 11 Phase B — Consent forms callout. Amber tint distinguishes it from
   the green-themed conversation jump button so the patient parses the two
   surfaces as distinct asks (forms = todo, conversation = ongoing). */
.pv-consent {
  display: flex;
  gap: 14px;
  margin: 10px 0 14px;
  padding: 14px 16px;
  background: #fef9f0;
  border: 1px solid #f1d8b1;
  border-radius: 10px;
  align-items: flex-start;
}
.pv-consent-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #fde9c5;
  color: #b45309;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pv-consent-icon .fa { font-size: 16px; }
.pv-consent-body { flex: 1; min-width: 0; }
.pv-consent-title {
  font-size: 14px;
  font-weight: 600;
  color: #78350f;
  letter-spacing: -0.1px;
  margin-bottom: 4px;
}
.pv-consent-sub {
  font-size: 12.5px;
  color: #92400e;
  margin: 0 0 9px;
  line-height: 1.5;
}
.pv-consent-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pv-consent-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid #f1d8b1;
  background: #fff;
  border-radius: 8px;
  color: #78350f;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  /* Slice 12 — this class now styles a <button> (opens the wizard iframe),
     so reset the UA button chrome. */
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
}
.pv-consent-link:hover {
  background: #fde9c5;
  border-color: #f59e0b;
  color: #78350f;
  text-decoration: none;
}
.pv-consent-link > .fa {
  color: #b45309;
  font-size: 13px;
}
.pv-consent-ext {
  margin-left: auto;
  font-size: 11px !important;
  color: #b45309;
  opacity: 0.7;
}
/* Slice 12 — completed consent form: non-link "Signed" row (green, muted). */
.pv-consent-done {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid #cdeee2;
  background: #f0faf6;
  border-radius: 8px;
  color: #6b7785;
  font-size: 13px;
  font-weight: 500;
}
.pv-consent-done > .fa {
  color: #10b981;
  font-size: 14px;
}
.pv-consent-done-name { color: #475569; }
.pv-consent-done-tag {
  margin-left: auto;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #047857;
}

.pv-toolbar {
  margin: 4px 0 18px;
  display: flex;
}
.pv-view-switcher {
  display: inline-flex;
  background: #f1f3f6;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}
.pv-view-tab {
  padding: 6px 14px;
  background: transparent;
  border: none;
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
}
.pv-view-tab:hover { color: #0f172a; }
.pv-view-tab.active {
  background: white;
  color: #094840;
  box-shadow: 0 1px 2px rgba(15,23,42,0.04);
}

.pv-paper-greet {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 18px;
  color: #0d5f54;
  margin-bottom: 6px;
}
.pv-paper-intro {
  font-size: 13.5px;
  color: #334155;
  line-height: 1.65;
  margin-bottom: 22px;
}

/* Payment options */
.pv-payments { margin-top: 28px; }
.pv-section-label {
  font-size: 11px;
  color: #6b7785;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 600;
  margin-bottom: 8px;
}
.pv-payment-helper {
  font-size: 12.5px;
  color: #475569;
  margin: 0 0 12px;
  line-height: 1.5;
}
.pv-payment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
/* Flex column + .pv-payment-foot { margin-top: auto } so every card's price and
   CTA sit on the card's bottom edge — a row of cards with different amounts of
   prose (or a finance tier table) still lines its numbers and buttons up.
   position: relative stays: both badges are absolutely positioned to it. */
.pv-payment-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #d4cfc3;
  border-radius: 8px;
  padding: 14px;
  background: #fff;
}
.pv-payment-foot { margin-top: auto; }
.pv-payment-card.recommended { border-color: #0d5f54; border-width: 2px; }
.pv-payment-badge {
  position: absolute;
  top: -10px;
  left: 12px;
  background: #0d5f54;
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
/* "Chosen" badge — mirrors Present mode's pm-payment-chosen-badge. */
.pv-payment-chosen-badge {
  position: absolute;
  top: -10px;
  left: 12px;
  background: #0d9488;
  color: #fff;
  padding: 2px 8px 2px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.pv-payment-chosen-badge .fa { font-size: 9px; }
.pv-payment-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 14px;
  font-weight: 600;
  color: #0d5f54;
}
.pv-payment-desc {
  font-size: 11.5px;
  color: #6b7785;
  margin-top: 4px;
  line-height: 1.5;
}
/* The card's price block: headline amount + qualifying sub-line. Lives in the
   foot, directly above the CTA. */
.pv-payment-amount {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
}
.pv-pay-amt {
  font-size: 16px;
  font-weight: 700;
  color: #0d5f54;
  font-variant-numeric: tabular-nums;
}
.pv-pay-sub {
  font-size: 11px;
  color: #6b7785;
}
.pv-payment-terms {
  font-size: 10.5px;
  color: #94a3b8;
  margin-top: 6px;
  line-height: 1.5;
}

/* Multi-tier financing menu — one row per term with its estimated monthly. */
.pv-finance-tiers {
  margin-top: 10px;
  border-top: 1px solid #eef1f4;
}
.pv-finance-tier {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 5px 0;
  border-bottom: 1px solid #f1f4f7;
  font-size: 12.5px;
}
.pv-finance-tier:last-child { border-bottom: none; }
.pv-finance-tier-label { color: #475569; }
.pv-finance-tier-amt {
  font-weight: 600;
  color: #101820;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Read-only Additional Fee row — practice opted in and the office set
   a per-patient amount in the Builder. Same amber palette as Present Mode. */
.pv-payment-fee {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 6px 0 4px;
  padding: 6px 8px;
  background: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: 6px;
  font-size: 11.5px;
  color: #78350f;
}
.pv-payment-fee .fa { font-size: 11px; color: #b45309; }
.pv-payment-fee-label { flex: 1; }
.pv-payment-fee-amt {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.pv-payments-cta {
  font-size: 12px;
  color: #94a3b8;
  margin: 18px 0 0;
  font-style: italic;
  line-height: 1.5;
  text-align: center;
}

/* Slice 7c — interactive payment cards */
.pv-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.pv-section-head .pv-section-label { margin-bottom: 0; }
.pv-saving,
.pv-saved {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  color: #94a3b8;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}
.pv-saved {
  color: #047857;
}
.pv-saving .fa,
.pv-saved .fa { font-size: 11px; }

.pv-payment-card.selected {
  border-color: #0d5f54 !important;
  border-width: 2px;
  background: #f0f9f6;
  box-shadow: 0 4px 12px rgba(13, 95, 84, 0.10);
}
.pv-payment-card.selected.recommended {
  background: #ecf5f3;
}

.pv-payment-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 12px;
  padding: 8px 12px;
  border: 1px solid #d4cfc3;
  background: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
  font-family: inherit;
}
.pv-payment-cta:hover:not(:disabled) {
  background: #ecf5f3;
  border-color: #0d5f54;
  color: #0d5f54;
}
.pv-payment-cta:disabled { opacity: 0.6; cursor: wait; }
.pv-payment-cta.active {
  background: #0d5f54;
  border-color: #0d5f54;
  color: #fff;
}
.pv-payment-cta.active:hover:not(:disabled) {
  background: #094840;
  border-color: #094840;
  color: #fff;
}
.pv-payment-cta .fa { font-size: 12px; }

/* Slice 8a — patient-side comments thread (renders only when there's
   already a conversation; compose box lands in 8b). */
.pv-thread {
  margin-top: 28px;
  padding: 22px 24px;
  background: #f8fafc;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}
.pv-thread-heading {
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 600;
  color: #0d5f54;
}
.pv-thread-heading .fa { margin-right: 8px; }
.pv-thread-body {
  min-height: 80px;
  max-height: 480px;
  /* overflow:auto on the OUTER container so the scrollbar is obvious to
     patients (the inner .ct-messages also scrolls, but with hidden the
     edge-of-content cue was missing and new messages went below the visible
     480px window). CommentsThread auto-scrolls to the bottom on new
     messages, so users see their own bubble + AI reply pop into view. */
  overflow-y: auto;
}

/* Footer */
.pv-footer {
  margin-top: 32px;
  padding-top: 18px;
  border-top: 1px solid #e4e7ec;
  text-align: center;
}
.pv-footer-practice {
  font-size: 12px;
  font-weight: 600;
  color: #0d5f54;
  margin-bottom: 4px;
}
.pv-footer-meta {
  font-size: 11px;
  color: #94a3b8;
}

/* Slice 7d — sign state overlays + revisit banner + PDF sig block */
.pv-success {
  max-width: 520px;
  margin: 80px auto;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 14px;
  padding: 36px 28px;
  text-align: center;
  box-shadow: 0 8px 28px rgba(15,23,42,0.06);
}
.pv-success-icon .fa {
  font-size: 56px;
  color: #10b981;
  margin-bottom: 12px;
}
.pv-success h2 {
  margin: 0 0 8px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 22px;
  color: #0d5f54;
  font-weight: 600;
}
.pv-success p {
  margin: 6px 0;
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
}
.pv-success-meta {
  font-size: 13px;
  color: #64748b;
}
.pv-success-meta-strong {
  margin-top: 14px;
  font-size: 12px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 500;
}
.pv-success-btn {
  margin-top: 20px;
  padding: 10px 22px;
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
}
.pv-success-btn:hover { background: #f1f3f6; color: #0d5f54; }

.pv-revisit-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 28px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #ecf5f3;
  border: 1px solid #b9dcd5;
  color: #094840;
  font-size: 12.5px;
  line-height: 1.5;
}
.pv-revisit-banner .fa {
  font-size: 16px;
  color: #0d5f54;
  margin-top: 1px;
  flex-shrink: 0;
}
.pv-revisit-text strong { font-weight: 600; }

/* Slice 12 — pre-sign consent nudge (slim fixed-bottom bar). */
.pv-sticky-consent {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1400;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 18px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  background: #78350f;
  color: #fff;
  box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.18);
}
.pv-sticky-text {
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
}
.pv-sticky-text .fa { font-size: 14px; opacity: 0.9; flex-shrink: 0; }
.pv-sticky-cta {
  flex-shrink: 0;
  padding: 8px 16px;
  background: #fff;
  border: none;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: #78350f;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s;
}
.pv-sticky-cta:hover { background: #fde9c5; }
.pv-sticky-x {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s;
}
.pv-sticky-x:hover { background: rgba(255, 255, 255, 0.28); }

@media (max-width: 600px) {
  .pv-paper { padding: 22px 18px 28px; }
  .pv-practice-logo { width: 38px; height: 38px; font-size: 16px; }
  .pv-practice-name { font-size: 16px; }
  .pv-paper-greet { font-size: 16px; }
  .pv-sticky-text { font-size: 12px; }
  .pv-sticky-cta { padding: 8px 14px; }
}

/* ─── Print ───────────────────────────────────────────
   Strip the app chrome + interactive controls and let the white "paper"
   fill the page as a clean document. Prints the currently-selected view. */
@media print {
  .pv {
    background: #fff !important;
    padding: 0 !important;
  }
  .pv-paper {
    max-width: 100% !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
  /* Anything explicitly marked, plus the interactive surfaces. */
  .pv-no-print,
  .pv-toolbar,
  .pv-jump-conversation,
  .pv-consent,
  .pv-payment-cta,
  .pv-payments-cta,
  .pv-saving,
  .pv-saved { display: none !important; }

  /* Keep brand colors (teal accents, Plan Summary bar) in the printout. */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Don't split a procedure row, payment card, or the summary across pages. */
  .pv-payment-card,
  .pv-payments { break-inside: avoid; }
  .pv-footer { margin-top: 18px; }
}
</style>
